/* eslint-disable react/function-component-definition */
import React, {
  Children,
  cloneElement,
  useState,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { clamp } from '../../utilities/UnitHelper';

const END = 35;
const HOME = 36;
const UP = 38;
const DOWN = 40;

/**
 * Accordion is a fully accessible W3C a11y compliant accordion component.  It has a direct dependency of
 * the AccordionItem.
 */
const Accordion = (props) => {
  const { expandAllItems, children, ...rest } = props;
  const [allExpanded, setAllExpanded] = useState(false);
  const accordionRef = useRef(null);
  const keyCodes = [END, HOME, UP, DOWN];

  const itemsArray = Children.toArray(children).filter(
    (c) => c.type.displayName && c.type.displayName.includes('AccordionItem')
  );

  const [items, _setItems] = useState(
    itemsArray.reduce((acc, child) => {
      acc[child.props.id] = {
        // eslint-disable-next-line react/destructuring-assignment
        expanded: props.expandAllItems ? true : !!child.props.expanded,
      };
      return acc;
    }, {})
  );

  const refItems = useRef(items);

  const setItems = (p) => {
    refItems.current = p;
    _setItems(p);
  };

  /**
   * Handles the keydown/keyup events and walks through the active element on the document
   * @param {object} event
   */
  const handleKeyEvent = (event) => {
    // exit if this is not a permitted keyCode
    if (!keyCodes.includes(event.keyCode)) {
      return;
    }

    const { activeElement } = document;
    const accordion = accordionRef.current;
    const childArray = accordion.children;

    event.preventDefault();

    let activeIndex = 0;

    for (let i = 0; i < childArray.length; i += 1) {
      if (childArray[i].firstChild === activeElement) {
        activeIndex = i;
        break;
      }
    }

    const { firstChild } = childArray[0];
    const lastChild = childArray[childArray.length - 1].firstChild;
    const prevIndex = clamp(activeIndex - 1, activeIndex);
    const nextIndex = clamp(activeIndex + 1, childArray.length - 1);

    // the default case here will never be reached, since the key codes
    // are checked first in this function.  disabling it here for cosmetics
    // so that it doesn't reduce coverage.
    // eslint-disable-next-line default-case
    switch (event.keyCode) {
      case HOME:
        firstChild.focus();
        break;
      case END:
        lastChild.focus();
        break;
      case UP:
        if (activeIndex === 0) {
          break;
        }
        childArray[prevIndex].firstChild.focus();
        break;
      case DOWN:
        if (activeIndex === childArray.length - 1) {
          break;
        }
        childArray[nextIndex].firstChild.focus();
        break;
    }
  };

  // run only on mount / unmount just for the key handlers
  useEffect(() => {
    const localRef = accordionRef.current;
    localRef.addEventListener('keydown', handleKeyEvent, true);
    return () => {
      localRef.removeEventListener('keydown', handleKeyEvent, true);
    };
  }, []);

  // collapses all items and set focus to false, since focus is on the
  // collapse all button
  const collapseAll = () => {
    Object.keys(items).forEach((k) => {
      items[k] = { expanded: false };
    });
    setAllExpanded(false);
    setItems({ ...items });
  };

  // expands all items, set focus to false, since focus is either on
  // the expand all button or we're on initial load
  const expandAll = () => {
    Object.keys(items).forEach((k) => {
      items[k] = { expanded: true };
    });
    setAllExpanded(true);
    setItems({ ...items });
  };

  // get a count of all expanded items.
  const getExpanded = () =>
    Object.keys(items).filter((item) => items[item].expanded === true).length;

  /**
   * onToggle handles the clicking of an accordion item title
   *
   * @param {string} id the id prop of the item
   */
  const onToggle = (id) => {
    Object.keys(items).forEach((item) => {
      // Check first if ALL items have been expanded
      if (allExpanded) {
        // We'll need to first collapse the current item
        items[id].expanded = !items[id].expanded;
        // Then we need to check how many items are expanded in total
        // If we have only one expanded item, then we can reset
        // allExpanded to false
        if (getExpanded() === 1) {
          setAllExpanded(false);
        }
      } else if (item === id || items[item].expanded) {
        // If we're in single selection mode and we're either on
        // the currently clicked item (item === id ) or if there
        // is another expanded item (items[item].expanded === true) collapse them
        items[item].expanded = !items[item].expanded;
      }
      // Reset the items state
      setItems({ ...items });
    });
  };

  // convert the children to an array so we can loop through them
  const childArray = Children.toArray(children);
  return childArray.length === 0 ? null : (
    <div ref={accordionRef} {...rest}>
      {childArray.map((child) => {
        if (!child.type.displayName) {
          return cloneElement(child, {});
        }
        if (child.type.displayName.includes('AccordionControls')) {
          return cloneElement(child, {
            expandAll,
            collapseAll,
            allExpanded,
            key: 'accordionControls',
          });
        }
        if (
          child.type.displayName.includes('AccordionItem') &&
          child.props.id !== undefined &&
          child.props.id !== ''
        ) {
          return cloneElement(child, {
            expanded: items[child.props.id].expanded,
            key: child.props.id,
            onToggle,
          });
        }
        return null;
      })}
    </div>
  );
};

Accordion.displayName = 'Accordion';

Accordion.propTypes = {
  /** The Accordion Items */
  children: PropTypes.node,
  /** Whether the accordion should start fully expanded */
  expandAllItems: PropTypes.bool,
};

Accordion.defaultProps = {
  children: null,
  expandAllItems: false,
};

/* @component */
export default Accordion;
